import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import { AccountsContext } from '../../components/context/account-context';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { formatUTCTimestampToLocal, getDateRangeUTC } from '../../utils/set-date-range-utils';
import logo from '../../assets/images/logo.jpg';
import { getAllReminderStatuses, getAllRenewalReminder } from '../../services/reminder-service';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import ReminderTable from './reminder-table';
import Help from './renewal-help';
import { usePageHelp } from '../../components/context/help-context';

const RenewalReminders = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [renewalReminderData, setRenewalReminderData] = useState([]);
    const [error, setError] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState({});

    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!accountId) return;
            setLoading(true);

            try {
                const [statusOptionsRes] = await Promise.all([
                    getAllReminderStatuses(accountId),
                ]);
                if (statusOptionsRes.success) setStatusOptions(statusOptionsRes.data.data || []);
            } catch (err) {
                console.error("Failed to fetch global data", err);
                setError('Failed to fetch global data');
            } finally {
                setLoading(false);
            }
        };

        fetchGlobalData();
    }, [accountId]);

    const fetchRenewalReminders = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);
            const response = await getAllRenewalReminder(accountId, from, to, 'renewal');
            console.log("renewal remdiners : ", response)
            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
                setRenewalReminderData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch renewal reminder records');
            }
        } catch (err) {
            setError('An error occurred while fetching renewal reminder records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRenewalReminders();
    }, [accountId, dateRange]);

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? renewalReminderData.filter((_, index) => selectedRows[index])
        : renewalReminderData;

    const csvData = exportData.map(item => ({
        'Reminder Id': item.reminderId || '',
        'Vehicle': item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
        'Linked Type': item.linkedType || '',
        'Reminder Type': item.reminderType || '',
        'Due Date': formatUTCTimestampToLocal(item.dueTime, 'yyyy-MM-dd hh:mm a'),
        'Status': camelCaseToWords(item.status || ''),
    }));


    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Renewal Reminders Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range placement
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Reminder Id', 'Vehicle', 'Linked Type', 'Reminder Type', 'Due Date', 'Status']],
            body: exportData.map(item => [
                item.reminderId || '',
                item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
                item.linkedType || '',
                item.reminderType || '',
                formatUTCTimestampToLocal(item.dueTime, 'yyyy-MM-dd hh:mm a'),
                camelCaseToWords(item.status || ''),
            ]),
            startY: dateRangeY + 5,
        });

        // Save with dynamic filename
        const fileName = `renewal-reminders-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Renewal Reminders</Heading>
                </div>
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate
                            }}
                        />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`renewal-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <ReminderTable
                    data={renewalReminderData}
                    setData={setRenewalReminderData}
                    fetchData={fetchRenewalReminders}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    loadingData={loading}
                    accountId={accountId}
                    statusOptions={statusOptions}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />
            </div>
        </div>
    );
};

export default RenewalReminders;